// Écran « Démarrage » (10/10/2026) : les étapes d'un nouvel abonné, dans
// l'ordre, avec leur statut calculé d'après les données et un lien direct
// vers l'écran concerné. Tout est bloqué tant que le régime de TVA n'a jamais
// été confirmé (les écritures en dépendent) ; réglage modifié depuis sa
// confirmation : bandeau « recochez », les autres étapes restent accessibles.
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";

const STATUS = {
  done:     { label: "✓ Fait",        color: "#16a34a", bg: "#dcfce7" },
  progress: { label: "En cours",      color: "#b45309", bg: "#fef3c7" },
  ready:    { label: "Disponible",    color: "#185fa5", bg: "#dbeafe" },
  todo:     { label: "À faire",       color: "#6b7280", bg: "#f3f4f6" },
  blocked:  { label: "🔒 Bloqué",     color: "#9ca3af", bg: "#f3f4f6" },
};

// Ouverture directe d'une fenêtre sur l'écran cible (lue par Facturation / Achats)
export const INTENT_KEY = "execpro360.intent";

export default function Demarrage({ currentCompanyId, onNavigate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    api.demarrage.get(currentCompanyId).then(setData).catch(e => setError(e.message));
  }, [currentCompanyId]);
  useEffect(() => { load(); }, [load]);

  const go = (step) => {
    if (step.status === "blocked") return;
    try { if (step.intent) sessionStorage.setItem(INTENT_KEY, step.intent); } catch {}
    onNavigate && onNavigate(step.page);
  };
  const noOpening = async () => {
    if (!window.confirm("Confirmez-vous n'avoir aucune facture de l'exercice précédent encore impayée ?")) return;
    setBusy(true);
    try { setData(await api.demarrage.noOpening(currentCompanyId)); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!currentCompanyId) return <div className="alert info">Sélectionnez une société.</div>;
  if (!data) return <div style={{ padding: 40, color: "var(--text-muted)" }}>{error || "Chargement…"}</div>;
  const next = data.steps.find(s => !["done", "blocked"].includes(s.status));

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>Démarrage</h1>
      <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 18 }}>
        Suivez les étapes dans l'ordre : chaque statut se met à jour automatiquement. {data.completed}/{data.total} étape(s) terminée(s).
      </div>
      {data.regime_changed && (
        <div style={{ background: "#fef3c7", color: "#92400e", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 13 }}>
          ⚠ Votre réglage de TVA a changé depuis sa confirmation : recochez « Je confirme ce réglage » (étape 1). Vos autres étapes restent accessibles.
        </div>
      )}
      {!data.confirmed && !data.regime_changed && (
        <div style={{ background: "#fef3c7", color: "#92400e", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 13 }}>
          🔒 Commencez par confirmer votre régime de TVA : les étapes suivantes en dépendent et restent bloquées jusque-là.
        </div>
      )}
      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {data.steps.map((s, i) => {
          const st = STATUS[s.status] || STATUS.todo;
          const isNext = next && next.id === s.id;
          return (
            <div key={s.id} className="card" style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "14px 16px", opacity: s.status === "blocked" ? 0.6 : 1, border: isNext ? "2px solid #1a9e72" : undefined }}>
              <div style={{ width: 30, height: 30, borderRadius: 99, background: st.bg, color: st.color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{s.title}</span>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: st.bg, color: st.color, fontWeight: 600 }}>{st.label}</span>
                  {isNext && <span style={{ fontSize: 11, color: "#1a9e72", fontWeight: 700 }}>← étape suivante</span>}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>{s.detail}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                <button className={`btn${isNext ? " primary" : ""}`} disabled={s.status === "blocked"} onClick={() => go(s)}>
                  {s.status === "done" ? "Voir" : "Ouvrir"} →
                </button>
                {s.can_skip && s.status !== "blocked" && (
                  <button className="btn" style={{ fontSize: 11, padding: "2px 8px" }} disabled={busy} onClick={noOpening}>Aucune facture impayée</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 16 }}><button className="btn" onClick={load}>↻ Actualiser</button></div>
    </div>
  );
}
